/**
 * Ein Block je Anlage — als Bildkachel zum Ankreuzen oder als Ja/Nein-Karte,
 * danach das für ALLE Anlagen gleiche Folgefragen-Set.
 *
 * Hieß bis zum 19.08.2026 `SicherheitsAnlageBlock` und bediente die fünf
 * Hauptanlagen. Aus fünf sind 28 geworden; die Unterschiede zwischen ihnen
 * (Errichter ja/nein, Zusatzfeld) kommen ausschließlich aus `anlagenKatalog.ts`
 * und stehen NICHT als `if`-Zweige in dieser Datei.
 *
 * **Die Anlage wird am BILD gewählt, nicht an einem Kontrollkästchen.** Der
 * Auftraggeber hat für die Auswahl ausdrücklich Bildkacheln verlangt — dieselbe
 * Bildsprache wie `BauartklassePicker`, `StellungPicker` und
 * `AusstattungsKatalog`: Zeichnung in einem getönten Feld oben
 * (`ILLU.headerBg`), Beschriftung darunter, Rahmen 2 px, gewählt = Aubergine
 * plus Ring. Zwischenstand war eine Zeile mit 40-px-Symbol neben einem
 * Kontrollkästchen; bei 23 Einträgen las sich das als Liste mit Beiwerk, und
 * genau das war gemeint, als „Kacheln sollen Bilder zeigen" beanstandet wurde.
 * Die Zeichnung steht deshalb bei 84 px und trägt die Kachel — der Bildsatz
 * verschmiert unter ~56 px in der Feintextur (Mauerfugen, Manometerzeiger).
 *
 * Zwei Bedienformen, weil die Spezifikation zwei verlangt:
 *  - `jaNein`   — die fünf Hauptanlagen: jede wird ausdrücklich mit Ja oder
 *                 Nein beantwortet, ein „nein" ist eine erfasste Antwort. Diese
 *                 Karte bleibt breit und liegend (Bild links, Frage rechts):
 *                 eine Kachel, die man anklickt, wäre hier eine Falschaussage —
 *                 anklicken hieße „ja", und „nein" ließe sich nicht sagen.
 *  - `checkbox` — die 23 Katalog-Anlagen unter „Welche der folgenden Anlagen
 *                 sind vorhanden?": die Kachel IST der Schalter
 *                 (`role="checkbox"` auf dem Knopf, wie im
 *                 `AusstattungsKatalog`). Anhaken setzt `vorhanden: "ja"`,
 *                 Abwählen ruft `onChange(undefined)`. Für eine nicht gewählte
 *                 Anlage entsteht damit KEIN Merkmal — `JSON.stringify`
 *                 verwirft `undefined` in Objekten, das Merkmal geht also gar
 *                 nicht erst auf die Reise. Ein `{ vorhanden: "nein" }` je
 *                 nicht gewählter Anlage ergäbe sonst 23 leere Merkmale pro
 *                 Gebäude.
 *
 * Der Haken links der Beschriftung ist Absicht und nicht Zierrat: gewählt/nicht
 * gewählt hinge sonst allein an der Rahmenfarbe. Er ist `aria-hidden`, der
 * Zustand steht für Hilfsmittel in `aria-checked`.
 */
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import UploadField from "./UploadField";
import { AnlagenIcon, type AnlagenIconKey } from "./anlagenIcons";
import { ILLU } from "./illustrationTokens";
import type { AnlagenExtraFeld } from "./anlagenKatalog";
import type { Ansprache } from "./texte";
import { useUploadBuffer, type UploadScope } from "./uploadBuffer";
import { ziffernFilter } from "./GrossZahlEingabe";

/**
 * Untergrenze einer Spalte im Detailblock.
 *
 * Der Umbruch richtet sich nach der Breite des CONTAINERS, nicht nach der des
 * Viewports: das Formular läuft auch im schwebenden Fenster, das sich auf
 * 420 px herunterziehen lässt, während der Viewport breit bleibt. Die frühere
 * `@media (min-width: 640px)` an dieser Stelle lieferte dort zwei Spalten in
 * einem ~350 px schmalen Bereich. Mittel und Begründung stehen ausführlich in
 * `geschossPaarLayout.ts`; `min(…, 100%)` verhindert, dass ein blankes
 * `minmax(260px, 1fr)` im schmalen Container über den Rand hinausläuft.
 *
 * In der aufgeklappten Kachel (~250 px innen) greift genau dieses `min()` und
 * stellt die Felder einspaltig untereinander; in der breiten Ja/Nein-Karte
 * (920 px Lesespalte) stehen dieselben Felder dreispaltig.
 */
const SPALTE_MIN_PX = 260;
const DETAIL_RASTER = `repeat(auto-fit, minmax(min(${SPALTE_MIN_PX}px, 100%), 1fr))`;

const useStyles = makeStyles({
  /**
   * Nur Klammer um Kachel und Folgefragen — den Rahmen trägt die Kachel selbst.
   * Zwei ineinanderliegende Rahmen machten den Aktiv-Zustand schwerer lesbar
   * (dieselbe Aufteilung wie im `AusstattungsKatalog`).
   */
  huelle: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  kachel: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    textAlign: "left",
    padding: "0",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    minWidth: 0,
    overflow: "hidden",
    ...shorthands.border("2px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("12px"),
    transitionProperty: "border-color, box-shadow",
    transitionDuration: "150ms",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
    },
    ":focus-visible": {
      ...shorthands.outline("2px", "solid", BRAND.purple),
      outlineOffset: "2px",
    },
  },
  /** Gewählt: Rahmen plus Ring — bewusst ohne Flächentönung, die Tönung des
      Bildfeldes würde den Kontrast oben sonst schlucken. */
  aktiv: {
    ...shorthands.borderColor(BRAND.purple),
    boxShadow: `0 0 0 4px ${BRAND.purple}22`,
  },
  /** Getöntes Bildfeld — kühl-neutral, nicht das Flieder der Auswahlzustände. */
  bildFeld: {
    backgroundColor: ILLU.headerBg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "10px",
    paddingRight: "10px",
  },
  // Feste Kantenlänge statt „mitwachsend": alle Kacheln einer Reihe zeigen die
  // Zeichnung damit gleich groß, unabhängig von der Länge der Beschriftung.
  bildSvg: {
    width: "84px",
    height: "84px",
  },
  kachelFuss: {
    display: "flex",
    alignItems: "flex-start",
    columnGap: "8px",
    paddingTop: "10px",
    paddingBottom: "12px",
    paddingLeft: "12px",
    paddingRight: "12px",
  },
  haken: {
    boxSizing: "border-box",
    width: "18px",
    height: "18px",
    flexShrink: 0,
    marginTop: "1px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: BRAND.white,
    ...shorthands.border("2px", "solid", "#d4cfd9"),
    ...shorthands.borderRadius("4px"),
  },
  hakenAktiv: {
    backgroundColor: BRAND.purple,
    ...shorthands.borderColor(BRAND.purple),
  },
  /**
   * `overflowWrap: anywhere` ist Pflicht, nicht Feinschliff: „Fehlerstrom- oder
   * Fehlerspannungsschutzeinrichtungen" enthält ein 33 Zeichen langes Wort und
   * liefe in einer 260-px-Kachel sonst über den Rand.
   */
  kachelTitel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    lineHeight: 1.3,
    color: BRAND.ink,
    minWidth: 0,
    // Zwei Zeilen Mindesthöhe: die Kacheln einer Reihe richten sich am oberen
    // Rand aus (`alignItems: start` im Raster, damit eine aufgeklappte Kachel
    // ihre Nachbarinnen nicht mitzieht). Ohne die Reserve endete jede Kachel
    // auf einer anderen Höhe, je nachdem ob ihre Beschriftung ein- oder
    // zweizeilig umbricht — bei 23 Kacheln ein sichtbar ausgefranster Rand.
    minHeight: "2.6em",
    overflowWrap: "anywhere",
    hyphens: "auto",
  },
  /** Liegende Karte der fünf Hauptanlagen: Bild links, Frage rechts. */
  jaNeinKarte: {
    backgroundColor: BRAND.white,
    display: "flex",
    alignItems: "center",
    columnGap: "14px",
    rowGap: "10px",
    flexWrap: "wrap",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "10px",
    paddingRight: "14px",
    ...shorthands.border("2px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("12px"),
  },
  bildFeldKlein: {
    backgroundColor: ILLU.headerBg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "6px",
    paddingRight: "6px",
    ...shorthands.borderRadius("10px"),
  },
  bildSvgKlein: {
    width: "64px",
    height: "64px",
  },
  title: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.05rem",
    color: BRAND.ink,
    flexGrow: 1,
    minWidth: 0,
    overflowWrap: "anywhere",
  },
  radioRow: {
    display: "flex",
    columnGap: "14px",
  },
  radioOption: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    cursor: "pointer",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: BRAND.ink,
  },
  detail: {
    marginTop: "10px",
    paddingTop: "12px",
    paddingLeft: "14px",
    paddingRight: "14px",
    paddingBottom: "12px",
    backgroundColor: "#FFF8E8",
    ...shorthands.borderLeft("3px", "solid", BRAND.gold),
    ...shorthands.borderRadius("4px"),
    display: "grid",
    gridTemplateColumns: DETAIL_RASTER,
    gap: "10px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
    minWidth: 0,
  },
  fieldLabel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#5d5664",
  },
  // `boxSizing` ist Pflicht: in apps/web gilt border-box nur unter `.kvp-rd`,
  // ohne die Angabe liefe `width: 100%` plus Polsterung über die Karte hinaus.
  input: {
    width: "100%",
    boxSizing: "border-box",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    paddingTop: "7px",
    paddingBottom: "7px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.borderRadius("4px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    outlineWidth: 0,
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
    },
  },
  fullSpan: {
    gridColumn: "1 / -1",
  },
  checkboxRow: {
    display: "flex",
    columnGap: "16px",
    flexWrap: "wrap",
    paddingTop: "4px",
  },
  checkOption: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    cursor: "pointer",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.88rem",
    color: BRAND.ink,
  },
});

/** Haken in der gewählten Kachel. Dekorativ — den Zustand trägt `aria-checked`. */
const HakenGlyph = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true" focusable="false">
    <path
      d="M2.4 6.4 4.8 8.9 9.6 3.3"
      stroke={BRAND.white}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Der gespeicherte Wert EINER Anlage, abgelegt unter `sicherheit_<slug>`.
 *
 * Die Index-Signatur trägt die Zusatzfelder aus dem Katalog (`bauart` bei den
 * Steigleitungen). Ohne sie wäre der generische Schreibzugriff
 * `set({ [feld.key]: wert })` gegen `Partial<AnlagenEntry>` nicht typkonform —
 * ein berechneter Schlüssel ist für den Compiler `{ [x: string]: string }` und
 * passt auf kein Objekt ohne Index-Signatur.
 *
 * Sie lautet `string | boolean | undefined` und nicht `string | undefined`,
 * weil `attest` und `wartungsprotokolle` seit der ersten Fassung BOOLEANS sind
 * und so in den Bestandsdaten liegen. Auf `"ja"`/`"nein"` umzustellen wäre eine
 * stille Entwertung: `"nein"` ist truthy, jede vorhandene `!!wert.attest`-Prüfung
 * kippte ins Gegenteil.
 */
export type AnlagenEntry = {
  vorhanden?: "ja" | "nein";
  errichter?: string;
  wartungsintervall?: string;
  /**
   * „Wert (Neuwert, brutto)". Steht IMMER im Detailblock und ausdrücklich
   * NICHT hinter „Liegen Unterlagen vor?" — der Wert ist eine Angabe zur
   * Anlage, keine Angabe zu den Nachweisen.
   */
  wert?: string;
  unterlagenLiegenVor?: "ja" | "nein";
  attest?: boolean;
  wartungsprotokolle?: boolean;
  sonstigeUnterlagen?: string;
  [k: string]: string | boolean | undefined;
};

export type AnlagenVariant = "jaNein" | "checkbox";

type Props = {
  /** Katalog-Slug — zugleich Motiv-Schlüssel des Bildsatzes. */
  anlage: AnlagenIconKey;
  title: string;
  value: AnlagenEntry;
  /**
   * `undefined` heißt „Merkmal löschen". Nur der Modus `checkbox` ruft das
   * beim Abwählen auf; der Aufrufer reicht es unverändert in den Antwort-Bag
   * durch, wo `JSON.stringify` es beim Speichern verwirft.
   */
  onChange: (next: AnlagenEntry | undefined) => void;
  /**
   * Ablage-Ebene der Nachweise: die Gebäude-UUID. Die Anlagen hängen am
   * Gebäude, nicht am Grundstück — bei mehreren Gebäuden landet jedes Attest
   * am RiskObject seines Gebäudes.
   */
  scope: UploadScope;
  /** Slot-Schlüssel ohne Gebäude-Scope, z. B. `sicherheit:bma`. */
  slotKey: string;
  /** Nur für den Hinweistext im Modus ohne Upload (E11). */
  ansprache?: Ansprache;
  /** Bedienform der Kopfzeile — siehe Dateikommentar. */
  variant?: AnlagenVariant;
  /** Marker „**" des Katalogs: `false` blendet den Errichter aus. */
  showErrichter?: boolean;
  /** Nur-für-diese-Anlage-Felder aus dem Katalog. */
  extraFields?: readonly AnlagenExtraFeld[];
};

const AnlagenBlock = ({
  anlage,
  title,
  value,
  onChange,
  scope,
  slotKey,
  ansprache = "kunde",
  variant = "jaNein",
  showErrichter = true,
  extraFields,
}: Props) => {
  const styles = useStyles();
  const { dropSlot } = useUploadBuffer();
  const set = (patch: Partial<AnlagenEntry>) => onChange({ ...value, ...patch });
  const offen = value.vorhanden === "ja";

  /**
   * Zurücknehmen heißt auch: die vorgemerkten Nachweise dieser Anlage wegwerfen.
   *
   * Drei Wege schließen den Nachweis-Bereich wieder — Kachel abwählen, „Nein"
   * bei einer Hauptanlage, „Liegen Unterlagen vor? Nein". Auf allen dreien
   * bliebe eine vorgemerkte Datei sonst im Puffer und landete nach dem
   * Speichern als Dokument am Risikoobjekt, obwohl es die Anlage dort nicht
   * gibt (`dropSlot` erklärt den Fall ausführlich). Bereits hochgeladene
   * Dokumente bleiben bewusst stehen.
   */
  const nachweiseVerwerfen = () => dropSlot(scope, slotKey);

  const detail = offen ? (
    <div className={styles.detail}>
      {(extraFields ?? []).map((feld) => {
        const name = `${title} — ${feld.label}`;
        const wert = typeof value[feld.key] === "string" ? (value[feld.key] as string) : "";
        return (
          <div key={feld.key} className={styles.field}>
            {/* Die Beschriftung steht NEBEN dem Feld, nicht in einem
                umschließenden <label> — sonst würde sie Teil des
                Feldnamens. Den Namen trägt `aria-label`, und er nennt
                zusätzlich die Anlage. */}
            <span className={styles.fieldLabel}>{feld.label}</span>
            {feld.type === "select" ? (
              <select
                className={styles.input}
                aria-label={name}
                value={wert}
                onChange={(e) => set({ [feld.key]: e.target.value })}
              >
                <option value="">— wählen —</option>
                {(feld.options ?? []).map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            ) : (
              <input
                className={styles.input}
                aria-label={name}
                value={wert}
                onChange={(e) => set({ [feld.key]: e.target.value })}
              />
            )}
          </div>
        );
      })}
      {showErrichter && (
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Errichter / Installationsfirma</span>
          <input
            className={styles.input}
            aria-label={`${title} — Errichter / Installationsfirma`}
            value={value.errichter ?? ""}
            onChange={(e) => set({ errichter: e.target.value })}
          />
        </div>
      )}
      <div className={styles.field}>
        <span className={styles.fieldLabel}>Wartungsintervall</span>
        <input
          className={styles.input}
          aria-label={`${title} — Wartungsintervall`}
          value={value.wartungsintervall ?? ""}
          onChange={(e) => set({ wartungsintervall: e.target.value })}
          placeholder="z. B. jährlich"
        />
      </div>
      <div className={styles.field}>
        <span className={styles.fieldLabel}>Wert (Neuwert, brutto)</span>
        <input
          className={styles.input}
          aria-label={`${title} — Wert (Neuwert, brutto)`}
          inputMode="decimal"
          placeholder="0,00"
          value={value.wert ?? ""}
          onChange={(e) => set({ wert: ziffernFilter(e.target.value, { dezimal: true }) })}
        />
      </div>
      <div className={mergeClasses(styles.field, styles.fullSpan)}>
        <span className={styles.fieldLabel}>Liegen Unterlagen vor?</span>
        <div className={styles.radioRow}>
          {[
            { v: "nein", label: "Nein" },
            { v: "ja", label: "Ja" },
          ].map((opt) => (
            <label key={opt.v} className={styles.radioOption}>
              <input
                type="radio"
                checked={value.unterlagenLiegenVor === opt.v}
                onChange={() => {
                  if (opt.v === "nein") nachweiseVerwerfen();
                  set({ unterlagenLiegenVor: opt.v as "ja" | "nein" });
                }}
                style={{ accentColor: BRAND.purple }}
              />
              {opt.label}
            </label>
          ))}
        </div>
      </div>
      {value.unterlagenLiegenVor === "ja" && (
        <>
          <div className={mergeClasses(styles.field, styles.fullSpan)}>
            <span className={styles.fieldLabel}>Welche Unterlagen?</span>
            <div className={styles.checkboxRow}>
              <label className={styles.checkOption}>
                <input
                  type="checkbox"
                  checked={!!value.attest}
                  onChange={(e) => set({ attest: e.target.checked })}
                  style={{ accentColor: BRAND.purple }}
                />
                Attest
              </label>
              <label className={styles.checkOption}>
                <input
                  type="checkbox"
                  checked={!!value.wartungsprotokolle}
                  onChange={(e) => set({ wartungsprotokolle: e.target.checked })}
                  style={{ accentColor: BRAND.purple }}
                />
                Wartungsprotokolle
              </label>
            </div>
          </div>
          <div className={mergeClasses(styles.field, styles.fullSpan)}>
            <span className={styles.fieldLabel}>Sonstige Unterlagen</span>
            <input
              className={styles.input}
              aria-label={`${title} — Sonstige Unterlagen`}
              value={value.sonstigeUnterlagen ?? ""}
              onChange={(e) => set({ sonstigeUnterlagen: e.target.value })}
            />
          </div>
          <div className={styles.fullSpan}>
            <UploadField
              scope={scope}
              slotKey={slotKey}
              label={`${title} – Atteste / Protokolle`}
              ansprache={ansprache}
            />
          </div>
        </>
      )}
    </div>
  ) : null;

  if (variant === "checkbox") {
    return (
      <div className={styles.huelle} role="group" aria-label={title}>
        <button
          type="button"
          role="checkbox"
          aria-checked={offen}
          className={mergeClasses(styles.kachel, offen && styles.aktiv)}
          onClick={() => {
            if (offen) {
              nachweiseVerwerfen();
              onChange(undefined);
            } else {
              set({ vorhanden: "ja" });
            }
          }}
        >
          {/* Die Zeichnung ist dekorativ (`aria-hidden` sitzt im SVG selbst):
              sie zeigt genau das, was die Beschriftung darunter sagt. Im
              Vorlesenamen der Kachel wäre ihre Bildbeschreibung eine Dublette
              — dieselbe Abwägung wie im `BauartklassePicker`. */}
          <span className={styles.bildFeld}>
            <AnlagenIcon anlage={anlage} className={styles.bildSvg} />
          </span>
          <span className={styles.kachelFuss}>
            <span
              className={mergeClasses(styles.haken, offen && styles.hakenAktiv)}
              aria-hidden="true"
            >
              {offen && <HakenGlyph />}
            </span>
            <span className={styles.kachelTitel}>{title}</span>
          </span>
        </button>
        {detail}
      </div>
    );
  }

  return (
    <div className={styles.huelle} role="group" aria-label={title}>
      <div className={mergeClasses(styles.jaNeinKarte, offen && styles.aktiv)}>
        <span className={styles.bildFeldKlein}>
          <AnlagenIcon anlage={anlage} className={styles.bildSvgKlein} />
        </span>
        <span className={styles.title}>{title}</span>
        <div className={styles.radioRow}>
          {[
            { v: "nein", label: "Nein" },
            { v: "ja", label: "Ja" },
          ].map((opt) => (
            <label key={opt.v} className={styles.radioOption}>
              <input
                type="radio"
                checked={value.vorhanden === opt.v}
                onChange={() => {
                  if (opt.v === "nein") nachweiseVerwerfen();
                  set({ vorhanden: opt.v as "ja" | "nein" });
                }}
                style={{ accentColor: BRAND.purple }}
              />
              {opt.label}
            </label>
          ))}
        </div>
      </div>
      {detail}
    </div>
  );
};

export default AnlagenBlock;
